import React, { memo } from 'react';
import { StyleSheet, View, TouchableOpacity, Pressable } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation, useRoute } from '@react-navigation/native';
import { useActionSheet } from '@expo/react-native-action-sheet';
import moment from 'moment';

import {
  selectCurrencies,
  selectExpenseList,
  selectExpenseStates,
} from '../../store/selectors/expense.selector';
import { DynamoDBFieldNames, LAYOUT } from '../../constants/ExpenseCategories';
import RowView from '../RowView';
import {
  customFieldDataTypes,
  EXPENSE_ACTION_TYPES,
  expenseInputTypes,
  searchTypesEnum,
} from '../../constants/Enums';
import { Modals } from '../../constants/ScreenNames';
import ExpenseFormItem, { ExpenseFormInput } from './ExpenseFormItem';
import { isNullOrEmpty } from '../../utils/common.utils';
import BaseText from '../BaseText';
import { Color, CommonProperties, CommonStyles } from '../../styles';
import { scale, scaleVertically } from '../../utils/scale.utils';
import { setCurrentReportIdForUpdate, setExpenseValue } from '../../store/actions/expense';
import { appendReportNumber, extractPaymentSuffix } from '../../utils/expense.utils';
import { expenseFieldHeight } from '../../styles/CommonProperties';
import { selectCompanyCurrency } from '../../store/selectors/auth.selector';

const { AIR, HOTEL, CAR, RAIL } = searchTypesEnum;

const translateCategories = (categories, categoryType) => {
  if (categoryType === 'Currency') {
    return categories.map((val) => `${val.CurrencyDesc} ${val.CurrencyCode}`);
  }
  return categories.map((val) => val);
};

const getReportBasedOnName = (name, expenseList) => {
  if (isNullOrEmpty(expenseList)) {
    return {};
  }
  if (!Array.isArray(expenseList)) {
    return {};
  }
  const findExpense = expenseList.find(
    (expense) => appendReportNumber(expense?.Name, expense?.ReportNumber) === name
  );
  return { ...findExpense, Name: name };
};

export const DynamicField = memo(
  ({ input, isEditable, value, currencyValue = null, actionType, subTab, style, index = 0 }) => {
    const navigation = useNavigation();
    const route = useRoute();
    const dispatch = useDispatch();
    const currencies = useSelector(selectCurrencies);
    const expenseList = useSelector(selectExpenseList);
    const companyCurrency = useSelector(selectCompanyCurrency);
    const { showActionSheetWithOptions } = useActionSheet();
    const MileageUnits = ['Mi', 'Km'];

    const { inputType, format } = input.inputConfig;
    const isMandatory =
      actionType === EXPENSE_ACTION_TYPES.CREATION
        ? input.mandatory.creation
        : input.mandatory.update; // need to use this to validate
    const label = isMandatory ? input.placeholder : `${input.placeholder} (Optional)`;

    const handleChangeDropdownCategory = (
      categories,
      categoryType,
      dropdownIndex = null,
      isCurrency = false
    ) => {
      const translatedCategories = translateCategories(categories, categoryType);
      showActionSheetWithOptions(
        {
          options: [...translatedCategories, 'Cancel'],
          cancelButtonIndex: translatedCategories.length,
          containerStyle: styles.dropdownContainer,
        },
        (buttonIndex) => {
          if (buttonIndex !== translatedCategories.length) {
            handleCategorySelection(
              buttonIndex,
              categories,
              categoryType,
              dropdownIndex,
              isCurrency
            );
          }
        }
      );
    };

    const handleCategorySelection = (
      buttonIndex,
      categories,
      categoryType,
      dropdownIndex,
      isCurrency
    ) => {
      let chosenCategory = categories[buttonIndex];
      switch (categoryType) {
        case 'report': {
          const report = getReportBasedOnName(chosenCategory, expenseList);
          dispatch(setCurrentReportIdForUpdate(report));
          break;
        }
        case DynamoDBFieldNames.DISTANCE_RATE:
          chosenCategory = categories[buttonIndex] || MileageUnits[0];
          dispatch(
            setExpenseValue({ actionType, subTab, key: categoryType, value: chosenCategory })
          );
          break;
        default:
          if (isCurrency) {
            chosenCategory = categories[buttonIndex]?.CurrencyCode || 'USD';
          }
          dispatch(
            setExpenseValue({ actionType, subTab, key: categoryType, value: chosenCategory })
          );
          break;
      }
    };

    switch (inputType) {
      case expenseInputTypes.TEXT_ENTRY:
        switch (format) {
          case customFieldDataTypes.DATE_TIME:
            return (
              <TouchableOpacity
                key={input.stateName}
                style={style}
                onPress={() => {
                  navigation.navigate(Modals.Calendar, {
                    returnScreen: route.name,
                    minDate: moment(new Date()).subtract(365, 'days').format('YYYY-MM-DD'),
                    maxDate: moment(new Date()).add(365, 'days').format('YYYY-MM-DD'),
                    pastScrollRange: 12,
                    futureScrollRange: 12,
                    fromDate: value,
                    isExpenseWidget: true,
                    stateName: input.stateName,
                    actionType,
                    subTab,
                  });
                }}
                disabled={!isEditable}
              >
                <ExpenseFormItem
                  label={label}
                  value={value ? moment(value).format('MMM DD') : null}
                  icon={input.icon}
                  disabled={!isEditable}
                />
              </TouchableOpacity>
            );
          default: {
            const isMultiline =
              !isNullOrEmpty(input.layoutConfig?.rowSpan) &&
              input.layoutConfig.rowSpan === LAYOUT.DOUBLE_HEIGHT;
            return (
              <ExpenseFormInput
                key={input.stateName}
                placeholder={label}
                value={value}
                onChangeText={(text) =>
                  dispatch(
                    setExpenseValue({ actionType, subTab, key: input.stateName, value: text })
                  )
                }
                icon={input.icon}
                isBuildingIcon={true}
                style={[
                  { height: isMultiline ? expenseFieldHeight * 2 : expenseFieldHeight },
                  style,
                ]}
                label={label}
                fromExpenseFormFields={true}
                disabled={!isEditable}
                // if the input has LAYOUT.DOUBLE_HEIGHT it will look like a text area input
                multiline={isMultiline}
              />
            );
          }
        }
      case expenseInputTypes.PULL_DOWN:
        return (
          <TouchableOpacity
            key={input.stateName}
            style={[styles.flex2, !isEditable && CommonStyles.disabledFormInput, style]}
            onPress={() => handleChangeDropdownCategory(input.inputConfig.options, input.stateName)}
            disabled={!isEditable}
          >
            <ExpenseFormItem label={label} value={value} isCaretDownIcon={isEditable} />
          </TouchableOpacity>
        );
      case expenseInputTypes.PAYMENT_TYPE:
        return (
          <TouchableOpacity
            key={input.stateName}
            style={[
              //   styles.flex2,
              //   { ...CommonProperties.expenseFieldHeight, paddingRight: scale(5) },
              !isEditable && CommonStyles.disabledFormInput,
              style,
            ]}
            onPress={() =>
              navigation.navigate(Modals.PaymentModal, {
                stateName: input.stateName,
                actionType,
                subTab,
                navigation,
              })
            }
            disabled={!isEditable}
          >
            <ExpenseFormItem
              label={label}
              value={value?.text ? extractPaymentSuffix(value.text) : extractPaymentSuffix(value)}
              disabled={!isEditable}
              isCaretDownIcon={isEditable}
              //   icon={input.icon}
            />
          </TouchableOpacity>
        );
      case expenseInputTypes.MONEY: {
        if (isNullOrEmpty(currencies)) {
          return null;
        }
        if (isNullOrEmpty(currencyValue)) {
          console.error('currencyValue must be passed for the money field');
          return null;
        }
        const isMileageUnit = input.stateName === DynamoDBFieldNames.RATE;
        return (
          <React.Fragment key={input.stateName}>
            <ExpenseFormInput
              label={label}
              placeholder={'0.00'}
              style={[styles.flex1, style]}
              keyboardType="decimal-pad"
              value={value}
              fromExpenseFormFields={true}
              isMoneyInput={true}
              currencyCode={currencyValue}
              companyCurrency={companyCurrency}
              onChangeText={(text) => {
                // Allow only numbers and a single dot
                const sanitizedValue = text.replace(/[^0-9.]/g, '');
                const parts = sanitizedValue.split('.');
                // Prevent multiple dots
                if (parts.length > 2) {
                  return;
                }
                // Limit to 2 decimals
                if (sanitizedValue.includes('.') && parts[1].length > 2) {
                  return;
                }

                dispatch(
                  setExpenseValue({
                    actionType,
                    subTab,
                    key: input.stateName,
                    value: sanitizedValue,
                  })
                );
              }}
              disabled={!isEditable}
              isMileageUnit={isMileageUnit}
            />

            <View style={CommonProperties.expenseFieldHeight}>
              <Pressable
                style={[styles.currencyPressable, index === 1 && styles.lastRightField]}
                onPress={() =>
                  handleChangeDropdownCategory(
                    isMileageUnit ? MileageUnits : currencies,
                    input.currencyStateName,
                    null,
                    true
                  )
                }
                disabled={!isEditable}
              >
                <BaseText style={styles.currencyText}>{currencyValue}</BaseText>
              </Pressable>
            </View>
          </React.Fragment>
        );
      }
      case expenseInputTypes.AUTO_COMPLETE: {
        const searchType = input.inputConfig.searchType;
        return (
          <TouchableOpacity
            style={[styles.flex1, !isEditable && CommonStyles.disabledFormInput]}
            onPress={() => {
              return navigation.navigate(Modals.StationAutoCompleteModal, {
                source: searchType === HOTEL ? 'to' : 'from',
                hasAir: searchType === AIR,
                hasHotel: searchType === HOTEL,
                hasCar: searchType === CAR,
                hasRail: searchType === RAIL,
                inputName: input.stateName,
                isExpenseWidget: true,
                actionType,
                subTab,
              });
            }}
            disabled={!isEditable}
          >
            <ExpenseFormItem label={label} icon={input.icon} value={value} />
          </TouchableOpacity>
        );
      }
      case expenseInputTypes.PARTICIPANT: {
        return (
          <TouchableOpacity
            style={[styles.flex1, !isEditable && CommonStyles.disabledFormInput]}
            onPress={() => {
              return navigation.navigate(Modals.ExpenseParticipantModal, {
                stateName: input.stateName,
                actionType,
                subTab,
                value,
              });
            }}
            disabled={!isEditable}
          >
            <ExpenseFormItem
              label={label}
              icon={input.icon}
              value={Array.isArray(value) ? value.join(', ') : value}
            />
          </TouchableOpacity>
        );
      }
      default:
        return null;
    }
  }
);

// Group fields for layout
const buildGroupedFields = (fields) => {
  const groupedFields = [];

  for (let i = 0; i < fields.length; i++) {
    const currentField = fields[i];
    const nextField = fields[i + 1];

    // Check if two consecutive fields are SINGLE_COLUMN
    if (
      nextField &&
      currentField.layoutConfig?.span === LAYOUT.SINGLE_COLUMN &&
      nextField.layoutConfig?.span === LAYOUT.SINGLE_COLUMN
    ) {
      groupedFields.push([currentField, nextField]); // Group these two fields
      i++; // Skip the next field since it's grouped
    } else {
      groupedFields.push([currentField]); // Single field in full-width rowPW
    }
  }

  return groupedFields;
};

const isEditableField = (stateName, isEditable, lockedFields) => {
  return isEditable && !lockedFields.includes(stateName);
};

const ExpenseDynamicFields = ({ step, actionType, subTab, isEditable, lockedFields = [] }) => {
  const expenseStates = useSelector(selectExpenseStates)[actionType][subTab];
  const currentSelectedSubCategory = expenseStates[DynamoDBFieldNames.SELECTED_SUBCATEGORY];
  const fields = currentSelectedSubCategory?.formFields?.[step] || [];
  const groupedFields = buildGroupedFields(fields);

  return (
    <>
      {groupedFields.map((group, groupIndex) => {
        // TODO: see if there's any improvement to be done here
        const key = `group-${groupIndex}`;
        const bottomBorderStyle = groupIndex < groupedFields.length - 1 ? styles.bottomBorder : {};
        if (group.length === 2) {
          // Render two fields in a single row
          return (
            <RowView style={[bottomBorderStyle, styles.alignCenter]} key={key}>
              {group.map((input, index) => (
                <React.Fragment key={input.stateName}>
                  <RowView style={{ width: '50%' }}>
                    <DynamicField
                      key={`dynamic${index}`}
                      input={input}
                      isEditable={isEditableField(input.stateName, isEditable, lockedFields)}
                      actionType={actionType}
                      subTab={subTab}
                      value={expenseStates[input.stateName]}
                      currencyValue={expenseStates?.[input?.currencyStateName]}
                      style={{ flex: 1 }}
                      index={index}
                    />
                  </RowView>
                  {index === 0 && <View style={CommonStyles.verticalDivider} />}
                </React.Fragment>
              ))}
            </RowView>
          );
        } else {
          // Render a single field (full-width)
          const input = group[0];
          const isMoneyField = input.inputConfig.inputType === expenseInputTypes.MONEY;
          return (
            <View style={[bottomBorderStyle, isMoneyField ? styles.rowStyle : {}]} key={key}>
              <DynamicField
                input={input}
                isEditable={isEditableField(input.stateName, isEditable, lockedFields)}
                actionType={actionType}
                subTab={subTab}
                value={expenseStates[input.stateName]}
                currencyValue={expenseStates?.[input?.currencyStateName]}
              />
            </View>
          );
        }
      })}
    </>
  );
};

export const styles = StyleSheet.create({
  flex1: {
    flex: 2,
  },
  flex2: {
    flex: 3,
  },
  dropdownContainer: {
    height: scaleVertically(300),
    backgroundColor: Color.accent,
  },
  currencyPressable: {
    backgroundColor: Color.grayText2,
    height: '100%',
    justifyContent: 'center',
    width: scale(45),
  },
  currencyText: {
    color: Color.accent,
    textAlign: 'center',
    ...CommonProperties.h2,
  },
  bottomBorder: {
    borderBottomWidth: 0.4,
    borderColor: Color.formDividerColor,
  },
  alignCenter: {
    alignItems: 'center',
  },
  rowStyle: {
    flexDirection: 'row',
  },
  lastRightField: {
    borderBottomRightRadius: 10,
  },
});

export default ExpenseDynamicFields;
